import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Check, Copy, ExternalLink, Loader2 } from 'lucide-react';
import { useT, type TFn } from '../i18n';

interface Props {
  widgetKey: string;
  /** Готовый URL widget.js — из env / production-конфига. */
  widgetSrc: string;
  /** Контролируется родителем — поллер /conversations выставляет true,
   *  когда в любом диалоге появляется хотя бы одно сообщение. */
  firstMessageReceived: boolean;
}

export function OnboardingView({ widgetKey, widgetSrc, firstMessageReceived }: Props) {
  const t = useT();
  const snippet = buildSnippet({ widgetKey, widgetSrc });

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="mx-auto max-w-2xl">
        <header className="mb-6 text-center">
          <h1 className="text-2xl font-semibold text-slate-900">{t('onboarding.title')}</h1>
          <p className="mt-2 text-sm text-slate-500">{t('onboarding.subtitle')}</p>
        </header>

        <ol className="space-y-4">
          <Step
            n={1}
            done={false}
            title={t('onboarding.step1.title')}
            hint={t('onboarding.step1.hint')}
          >
            <CodeSnippet code={snippet} t={t} />
          </Step>

          <Step
            n={2}
            done={false}
            title={t('onboarding.step2.title')}
            hint={t('onboarding.step2.hint')}
          />

          <Step
            n={3}
            done={firstMessageReceived}
            title={t('onboarding.step3.title')}
            hint={t('onboarding.step3.hint')}
          >
            {firstMessageReceived ? (
              <p className="inline-flex items-center gap-1.5 rounded-md bg-emerald-50 px-3 py-1.5 text-sm font-medium text-emerald-700">
                <Check size={14} aria-hidden="true" />
                {t('onboarding.step3.done')}
              </p>
            ) : (
              <p className="inline-flex items-center gap-1.5 text-sm text-slate-500">
                <Loader2 size={14} aria-hidden="true" className="animate-spin" />
                {t('onboarding.step3.waiting')}
              </p>
            )}
          </Step>
        </ol>

        <div className="mt-6 flex items-center justify-between">
          <Link
            to="/"
            className="text-xs text-slate-500 hover:text-slate-900"
          >
            {t('onboarding.skip')}
          </Link>
          {firstMessageReceived && (
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 rounded-md bg-accent-600 px-3.5 py-2 text-sm font-medium text-white hover:bg-accent-700"
            >
              <ExternalLink size={14} aria-hidden="true" />
              {t('onboarding.goToInbox')}
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}

function Step({
  n,
  done,
  title,
  hint,
  children,
}: {
  n: number;
  done: boolean;
  title: string;
  hint: string;
  children?: React.ReactNode;
}) {
  return (
    <li className="rounded-md border border-slate-200 bg-white p-4 shadow-card">
      <div className="flex items-start gap-3">
        <span
          className={
            'flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-sm font-medium ' +
            (done
              ? 'bg-emerald-100 text-emerald-700'
              : 'bg-slate-100 text-slate-600')
          }
          aria-hidden="true"
        >
          {done ? <Check size={14} /> : n}
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
          <p className="mt-1 text-sm text-slate-500">{hint}</p>
          {children && <div className="mt-3">{children}</div>}
        </div>
      </div>
    </li>
  );
}

function CodeSnippet({ code, t }: { code: string; t: TFn }) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard может быть недоступен (insecure context) — fallback на select
    }
  }

  return (
    <div>
      <pre className="overflow-x-auto rounded-md border border-slate-200 bg-slate-50 p-3 font-mono text-xs text-slate-700">
        <code>{code}</code>
      </pre>
      <button
        type="button"
        onClick={copy}
        className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
      >
        {copied ? (
          <>
            <Check size={12} aria-hidden="true" /> {t('onboarding.step1.copied')}
          </>
        ) : (
          <>
            <Copy size={12} aria-hidden="true" /> {t('onboarding.step1.copy')}
          </>
        )}
      </button>
    </div>
  );
}

function buildSnippet({ widgetKey, widgetSrc }: { widgetKey: string; widgetSrc: string }) {
  // type="module" обязателен — widget bundle собран как ES-module и
  // использует top-level `import`. Без него браузер парсит скрипт как
  // classic JS и валится с SyntaxError. defer не нужен — module-script'ы
  // и так deferred по умолчанию.
  return [
    `<script`,
    `  type="module"`,
    `  src="${widgetSrc}"`,
    `  data-key="${widgetKey}"`,
    `></script>`,
  ].join('\n');
}
